import { RenderPass } from "@/core/pipeline/render-pass"
import { Texture } from "@/core/backends/webgl/texture"
import { mat4 } from "gl-matrix"

export class TransformPass extends RenderPass {
	render(
		input: Texture,
		output?: Texture,
		options: {
			isForHealing?: boolean
			ignoreLensCorrection?: boolean
			disableSuperSampling?: boolean
		} = {},
	) {
		const { renderer } = this
		const { shaders, photo } = renderer
		const isForHealing = options.isForHealing ?? false

		this.renderer.performanceCounters.begin("transformPass")

		const renderDenoise =
			photo.type !== "bitmap" && !!(renderer.denoiseTextures && renderer.denoiseEnabled)

		if (output && !isForHealing) {
			renderer.alphaTexture.updateSize(output.width, output.height)
			renderer.setRenderTarget(output, [renderer.alphaTexture])
		} else {
			renderer.setRenderTarget(output)
		}

		input.bind(0)
		renderer.denoiseTextures?.denoiseLumTexture.bind(8)
		renderer.denoiseTextures?.denoiseColorTexture.bind(9)

		shaders.transformCorrection.setUniforms({
			inputTexture: 0,
			denoiseLumTexture: 8,
			denoiseColorTexture: 9,
			cropMatrix: isForHealing ? mat4.create() : renderer.cropMatrix,
			perspectiveMatrix: isForHealing ? mat4.create() : renderer.perspectiveMatrix,
			colorNoiseReduction: photo.adjustments.colorNoiseReduction,
			luminanceNoiseReduction: photo.adjustments.luminanceNoiseReduction,
			renderDenoise,
			forHealing: isForHealing,
			disableSuperSampling: options.disableSuperSampling ?? false,
		})

		shaders.transformCorrection.setUniforms(renderer.profileManager.getVignetteUniforms(photo))
		shaders.transformCorrection.setUniforms({
			...renderer.profileManager.getChromaticAberrationUniforms(photo),
			...(isForHealing ? { renderChromaticAberrationCorrection: false } : {}),
		})

		shaders.transformCorrection.setUniforms({
			...renderer.profileManager.getLensUniforms(photo),
			...(isForHealing || options.ignoreLensCorrection === true
				? {
						renderLensDistortionCorrection: false,
						lensDistortion: 0,
					}
				: {}),
		})

		shaders.transformCorrection.draw()

		this.renderer.performanceCounters.end("transformPass")
	}

	dispose() {
		return
	}
}
